import React, { ReactElement, useMemo, useState } from "react";
import { Col, Container, Row, Table, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Chart from "react-apexcharts";
import { StatsService } from "../stats";
import { UserService, IUserTableData } from "../users";
import { Loading } from "../ui";

interface ConversionSummary {
	leadsCreated: number;
	opportunitiesCreated: number;
	opportunitiesWon: number;
	leadToOppRate: number;
	oppToWonRate: number;
	overallRate: number;
}

interface TrendData {
	period: string;
	leads: number;
	opportunities: number;
	won: number;
	leadToOppRate: number;
	oppToWonRate: number;
}

interface RepData {
	name: string;
	leads: number;
	opportunities: number;
	won: number;
	conversionRate: number;
}

interface ConversionData {
	summary: ConversionSummary;
	trend: TrendData[];
	byRep: RepData[];
}

export const ConversionReportPage = (): ReactElement => {
	const [date, setDate] = useState<Date | null>(new Date());
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [months, setMonths] = useState<number>(6);
	const [users, setUsers] = useState<IUserTableData[]>([]);
	const [data, setData] = useState<ConversionData | null>(null);
	const [loading, setLoading] = useState(true);

	const statsService = new StatsService();
	const userService = new UserService();

	const fetchUsers = async () => {
		const userList = await userService.getUsersForTable(true);
		setUsers(userList.filter(u => u.include_in_dashboard_stats));
	};

	const fetchData = async () => {
		if (!date) return;
		setLoading(true);
		const result = await statsService.findConversionReport(date, userId, months);
		setData(result as ConversionData);
		setLoading(false);
	};

	useMemo(() => {
		fetchUsers();
	}, []);

	useMemo(() => {
		fetchData();
	}, [date, userId, months]);

	return (
		<Container fluid className="dashboard-container">
			<div className="dashboard-header-card">
				<div className="dashboard-header-main">
					<div className="dashboard-icon-wrapper">
						<i className="fa fa-exchange"></i>
					</div>
					<div className="dashboard-info">
						<h1 className="dashboard-title">Sales Conversion Report</h1>
						<p className="dashboard-subtitle">
							Track conversion rates from Leads to Opportunities to Won
						</p>
					</div>
				</div>
				<div className="dashboard-header-actions">
					<Form.Select
						className="me-2"
						style={{ width: "200px", display: "inline-block" }}
						value={userId || ""}
						onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : undefined)}
					>
						<option value="">All Sales Reps</option>
						{users.map((user) => (
							<option key={user.id} value={user.id}>
								{user.full_name}
							</option>
						))}
					</Form.Select>
						<div className="date-picker-wrapper">
						<i className="fa fa-calendar"></i>
						<DatePicker
							className="form-control date-picker-input"
							dateFormat="MM/yyyy"
							selected={date}
							showMonthYearPicker
							onChange={(_date: Date | null) => setDate(_date)}
						/>
					</div>
				</div>
			</div>

			<div className="dashboard-card mb-4">
				<div className="card-header">
					<i className="fa fa-info-circle"></i>
					<span>About This Report</span>
				</div>
				<div className="card-body">
					<p><strong>What this report shows:</strong> Tracks how effectively leads convert into opportunities and ultimately into closed-won deals. This is your key metric for measuring sales funnel efficiency.</p>
					<p><strong>How data is calculated:</strong></p>
					<ul>
						<li><strong>Leads Created:</strong> Total number of new leads created during the selected month.</li>
						<li><strong>Opportunities:</strong> Number of opportunities created during the selected month. The percentage shows what portion of leads became opportunities.</li>
						<li><strong>Won:</strong> Opportunities that were closed as won during the selected month. The percentage shows what portion of opportunities converted to sales.</li>
						<li><strong>Overall Conversion Rate:</strong> The end-to-end conversion from lead to won deal (Won ÷ Leads Created).</li>
						<li><strong>Monthly Trend:</strong> Shows the same metrics over the past N months (configurable via dropdown) to identify trends.</li>
					</ul>
					<p><strong>How to use:</strong> Monitor conversion rates to identify where leads drop off in your funnel. Compare performance across sales reps to identify coaching opportunities. Use the trend chart to spot seasonal patterns or the impact of process changes. A healthy funnel typically shows 20-30% lead-to-opportunity and 20-40% opportunity-to-won rates, though this varies by industry.</p>
				</div>
			</div>

			{loading ? (
				<div className="text-center p-5">
					<Loading /> Loading conversion data...
				</div>
			) : data ? (
				<>
					<Row className="mb-4">
						<Col lg={3}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-users"></i>
									<span>Leads Created</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-info" style={{ fontSize: "2.5rem" }}>
										{data.summary.leadsCreated}
									</h2>
								</div>
							</div>
						</Col>
						<Col lg={3}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-briefcase"></i>
									<span>Opportunities</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-warning" style={{ fontSize: "2.5rem" }}>
										{data.summary.opportunitiesCreated}
									</h2>
									<small className="text-muted">
										{data.summary.leadToOppRate}% of leads
									</small>
								</div>
							</div>
						</Col>
						<Col lg={3}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-trophy"></i>
									<span>Won</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-success" style={{ fontSize: "2.5rem" }}>
										{data.summary.opportunitiesWon}
									</h2>
									<small className="text-muted">
										{data.summary.oppToWonRate}% of opportunities
									</small>
								</div>
							</div>
						</Col>
						<Col lg={3}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-percent"></i>
									<span>Overall Conversion</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-primary" style={{ fontSize: "2.5rem" }}>
										{data.summary.overallRate}%
									</h2>
									<small className="text-muted">Lead to Won</small>
								</div>
							</div>
						</Col>
					</Row>

					<Row className="mb-4">
						<Col lg={6}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-filter"></i>
									<span>Conversion Funnel</span>
								</div>
								<div className="card-body">
									<Chart
										options={{
											chart: {
												type: "bar",
											},
											plotOptions: {
												bar: {
													borderRadius: 4,
													horizontal: false,
													columnWidth: "60%",
												},
											},
											xaxis: {
												categories: ["Leads", "Opportunities", "Won"],
											},
											colors: ["#17a2b8", "#ffc107", "#28a745"],
											dataLabels: {
												enabled: true,
											},
										}}
										series={[
											{
												name: "Count",
												data: [
													data.summary.leadsCreated,
													data.summary.opportunitiesCreated,
													data.summary.opportunitiesWon,
												],
											},
										]}
										type="bar"
										height={250}
									/>
								</div>
							</div>
						</Col>
						<Col lg={6}>
							<div className="dashboard-card">
								<div className="card-header d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-line-chart"></i>
										<span>Monthly Trend</span>
									</div>
									<Form.Select
										size="sm"
										style={{ width: "110px" }}
										value={String(months)}
										onChange={(e) => setMonths(Number(e.target.value))}
									>
										<option value="3">3 months</option>
										<option value="6">6 months</option>
										<option value="12">12 months</option>
									</Form.Select>
								</div>
								<div className="card-body">
									<Chart
										options={{
											chart: {
												type: "line",
											},
											xaxis: {
												categories: data.trend.map((t) => t.period),
											},
											colors: ["#17a2b8", "#ffc107", "#28a745"],
											stroke: {
												curve: "smooth",
												width: 2,
											},
											markers: {
												size: 4,
											},
											legend: {
												position: "top",
											},
										}}
										series={[
											{
												name: "Leads",
												data: data.trend.map((t) => t.leads),
											},
											{
												name: "Opportunities",
												data: data.trend.map((t) => t.opportunities),
											},
											{
												name: "Won",
												data: data.trend.map((t) => t.won),
											},
										]}
										type="line"
										height={250}
									/>
								</div>
							</div>
						</Col>
					</Row>

					<Row>
						<Col lg={12}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-users"></i>
									<span>Performance by Sales Rep</span>
								</div>
								<div className="card-body">
									<Table striped hover responsive>
										<thead>
											<tr>
												<th>Sales Rep</th>
												<th className="text-end">Leads</th>
												<th className="text-end">Opportunities</th>
												<th className="text-end">Won</th>
												<th className="text-end">Conversion Rate</th>
											</tr>
										</thead>
										<tbody>
											{data.byRep.map((rep) => (
												<tr key={rep.name}>
													<td>{rep.name}</td>
													<td className="text-end">{rep.leads}</td>
													<td className="text-end">{rep.opportunities}</td>
													<td className="text-end">{rep.won}</td>
													<td className="text-end">
														<span
															className={
																rep.conversionRate >= 10
																	? "text-success"
																	: rep.conversionRate >= 5
																	? "text-warning"
																	: "text-danger"
															}
														>
															{rep.conversionRate}%
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
									{data.byRep.length === 0 && (
										<div className="text-center text-muted p-3">
											No data available for this period
										</div>
									)}
								</div>
							</div>
						</Col>
					</Row>
				</>
			) : (
				<div className="text-center p-5">No data available</div>
			)}
		</Container>
	);
};
